import { useEffect, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { api } from '@/services/api';
import type { CanvasExpansionHistoryEntry } from '@the-wall/shared';
import { UserBadges } from '@/components/UserBadges';
import { UsernameText } from '@/components/UsernameText';

interface ExpansionHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function ExpandIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
      />
    </svg>
  );
}

// SQLite datetime('now') stores UTC without a 'Z' suffix; append one so JS parses as UTC.
function normalizeDate(dateString: string): Date {
  const normalized = dateString.endsWith('Z') ? dateString : dateString.replace(' ', 'T') + 'Z';
  return new Date(normalized);
}

function formatTimeAgo(date: Date): string {
  const diffMs = Date.now() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function ExpansionList({ entries }: { entries: CanvasExpansionHistoryEntry[] }) {
  if (entries.length === 0) {
    return <p className="text-wall-muted text-sm py-6 text-center">No expansions yet.</p>;
  }
  return (
    <ul className="divide-y divide-wall-border/50">
      {entries.map((entry) => {
        const triggeredBy = entry.triggeredBy;
        const when = normalizeDate(entry.createdAt);
        return (
          <li key={entry.expansionNumber} className="flex items-center gap-3 py-2.5 px-2 -mx-2">
            <span className="w-9 shrink-0 flex justify-center items-center">
              <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold leading-none bg-wall-accent/15 text-wall-accent border border-wall-accent/40">
                {entry.expansionNumber}
              </span>
            </span>
            <span className="h-8 w-8 shrink-0 rounded-full overflow-hidden bg-wall-border/50 flex items-center justify-center">
              {triggeredBy?.profilePicture ? (
                <img src={triggeredBy.profilePicture} alt="" className="h-full w-full object-cover" />
              ) : (
                <span className="text-xs font-medium text-wall-muted leading-none">
                  {triggeredBy ? triggeredBy.username.slice(0, 2).toUpperCase() : '··'}
                </span>
              )}
            </span>
            <span className="flex-1 min-w-0 flex flex-col gap-0.5 overflow-hidden">
              <span className="flex items-center gap-1.5 overflow-hidden">
                {triggeredBy ? (
                  <>
                    <UsernameText
                      text={triggeredBy.username}
                      color={triggeredBy.usernameColor ?? null}
                      effect={triggeredBy.usernameEffect ?? null}
                      className="truncate text-wall-text font-medium"
                    />
                    <UserBadges userId={triggeredBy.userId} className="shrink-0" />
                  </>
                ) : (
                  <span className="truncate text-wall-muted font-medium italic">
                    Automatic expansion
                  </span>
                )}
              </span>
              <span className="text-[11px] text-wall-muted tabular-nums">
                {entry.previousWidth}×{entry.previousHeight} → {entry.newWidth}×{entry.newHeight}
              </span>
            </span>
            <span
              className="shrink-0 text-wall-muted text-xs tabular-nums"
              title={when.toLocaleString()}
            >
              {formatTimeAgo(when)}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

export function ExpansionHistoryModal({ isOpen, onClose }: ExpansionHistoryModalProps) {
  const [entries, setEntries] = useState<CanvasExpansionHistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    },
    [handleClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
      setLoading(true);
      api
        .getCanvasExpansions()
        .then(setEntries)
        .catch(() => setEntries([]))
        .finally(() => setLoading(false));
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-md min-h-full"
        onClick={handleClose}
        aria-hidden
      />
      <div
        className="relative bg-wall-surface border border-wall-border rounded-2xl shadow-2xl w-full max-w-sm max-h-[85dvh] flex flex-col overflow-hidden my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="shrink-0 px-4 pt-4 pb-3 border-b border-wall-border/60 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ExpandIcon className="w-5 h-5 text-wall-accent" />
            <h2 className="text-lg font-semibold text-wall-text tracking-tight">
              Expansion history
            </h2>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="p-2 -mr-2 text-wall-muted hover:text-wall-text rounded-lg hover:bg-wall-border/30 transition-colors"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="h-[420px] overflow-y-auto px-4 py-3">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-wall-accent border-t-transparent" />
            </div>
          ) : (
            <ExpansionList entries={entries} />
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
